import { generateClient } from "aws-amplify/api";
import {
	FieldInstance,
	FieldTemplate,
	FieldTemplateForResolver,
	FormGroup,
	FormInstance,
	FormInstanceForResolver,
	FormTemplate,
	FormTemplateForResolver,
	Option,
} from "db/definitions";
import { FormInstanceAndFormTemplate } from "utils/batchGetFormInstancesByFormGroup";
import { db } from "../db";
import { bulkRetrieveFieldInstancesByFormGroup, deleteFieldInstance, getAllFieldsByFormInstanceId, getAllTableFieldChildren } from "./fieldInstanceQueries";
import { bulkRetrieveFieldTemplatesByFormTemplate } from "./fieldTemplateQueries";
import { createFormGroupForBatchResolver, updateFormGroup } from "./formGroupQueries";
import { bulkRetrieveFormTemplates, createMergedFormTemplate } from "./formTemplateQueries";
import { getImageDocWithBinaryKeyForUpdateImageDoc } from "./imageQueries";
import { bulkRetrieveOptionsByFormTemplateId } from "./optionQueries";
import { recordSentry } from "utils/recordSentry";

const client = generateClient({ authMode: "userPool" });

/**
 * Given the formGroup id, finds all formInstances and builds them like they were retrieved by aws so we can just plug and play
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the formGroup
 * @returns The built formInstances
 */
export async function buildFormInstancesByFormGroupId(id: string, getImageBinaries?: boolean) {
	// New attempt, where we grab all form/field instances by formGroup and organize them with each other.
	// Step 1: Get all raw formInstances where the formGroup property === our id
	// Step 2: Get all raw fieldInstances where the formGroup property === our id
	// Step 1: Get all formTemplates by formInstance Ids
	// Step 2: Get all fieldTemplates by formTemplate ids
	// Step 5: Map options by formTemplateId
	// Step 6: Map out fieldTemplates by formTemplateId
	// Step 7: Create formTemplate structure
	// Step 6: Return
	// const retrievedFormInstances = await getFormInstancesByFormGroup(id);

	// Step 1: Get all raw formInstances where the formGroup property === our id
	const retrievedFormInstances = await getFormInstancesByFormGroup(id);
	// Step 3: Get all formTemplates from the formInstances
	const formTemplateIds = retrievedFormInstances.map((formInstance) => formInstance.formTemplateId);
	// Step 4: Get all fieldTemplates by formTemplateId
	const fieldTemplatePromises = [];
	const optionPromises = [];
	for (const id of formTemplateIds) {
		fieldTemplatePromises.push(bulkRetrieveFieldTemplatesByFormTemplate(id));
		optionPromises.push(bulkRetrieveOptionsByFormTemplateId(id));
	}
	const [arrayOfFieldTemplateArrays, arrayOfOptionArrays, retrievedFieldInstances, formTemplates] = await Promise.all([
		await Promise.all(fieldTemplatePromises),
		await Promise.all(optionPromises),
		bulkRetrieveFieldInstancesByFormGroup(id),
		bulkRetrieveFormTemplates(formTemplateIds),
	]);
	// Step 5: Map options by formTemplateId. Note that the length of retrievedFormInstances, formTemplates, arrayOfFieldTemplateArrays, and arrayofOptionArrays should be the same length, where
	// index n is the complete set of everything for one form

	// Map out all Options, so we can attach options to the correct fieldTemplate by fieldTemplateId
	const fieldTemplateToOptions: Map<string, Option[]> = new Map();
	for (const optionArray of arrayOfOptionArrays) {
		for (const option of optionArray) {
			const options = fieldTemplateToOptions.get(option.fieldTemplateId);
			if (options) {
				options.push(option);
			} else {
				fieldTemplateToOptions.set(option.fieldTemplateId, [option]);
			}
		}
	}

	// Use this as quick access for the fieldInstances
	const fieldTemplateIdToTemplate: Map<string, FieldTemplateForResolver> = new Map();
	// Use these 2 to construct the formTemplate.fieldTemplates.items and the correct table structures
	const formTemplateToNormalFieldTemplates: Map<string, Map<string, FieldTemplate>> = new Map();
	const formTemplateToTableFields: Map<string, FieldTemplateForResolver[]> = new Map();
	// Step 6: Map out fieldTemplates by formTemplateId
	// Map out all fieldTemplates , assigning options. Normal fields are grouped by formTemplate key, with value being an inner map of fieldTemplate id to fieldTemplate
	// Table field fieldTemplates are kept in their own array, which we will iterate over after this and access the prior map by formTemplateId and the inner map by tableFieldId. Then,
	// we add it the that field's array. Clear as mud?
	for (const fieldTemplateArray of arrayOfFieldTemplateArrays) {
		for (const fieldTemplate of fieldTemplateArray) {
			// Makes typescript happy
			const fieldTemplateCopy: FieldTemplate = { ...fieldTemplate };
			const options = fieldTemplateToOptions.get(fieldTemplateCopy.id);
			fieldTemplateCopy.options = { items: options || [] };
			fieldTemplateCopy.tableFields = { items: [] };
			if (!fieldTemplateCopy.tableField) {
				const innerMap = formTemplateToNormalFieldTemplates.get(fieldTemplateCopy.formTemplate);
				if (innerMap) {
					innerMap.set(fieldTemplateCopy.id, { ...fieldTemplateCopy });
				} else {
					const innerMap = new Map();
					innerMap.set(fieldTemplateCopy.id, { ...fieldTemplateCopy });
					formTemplateToNormalFieldTemplates.set(fieldTemplateCopy.formTemplate, innerMap);
				}
			} else {
				const tableFields = formTemplateToTableFields.get(fieldTemplateCopy.formTemplate);
				if (tableFields) {
					formTemplateToTableFields.set(fieldTemplateCopy.formTemplate, [...tableFields, { ...fieldTemplateCopy }]);
				} else {
					formTemplateToTableFields.set(fieldTemplateCopy.formTemplate, [{ ...fieldTemplateCopy }]);
				}
			}
			fieldTemplateIdToTemplate.set(fieldTemplateCopy.id, { ...fieldTemplateCopy });
		}
	}

	// Loop through the map of the formTemplate's fieldTemplates that are children of fields. Find the parent tableField in the formTemplateToNormalFieldTemplates map. If that map has
	// the parent field, copy it, push a copy of the table field into the tableFields array, and update the map
	formTemplateToTableFields.forEach((tableFieldTemplateArray, formTemplateId) => {
		if (formTemplateToNormalFieldTemplates.has(formTemplateId)) {
			for (const tableField of tableFieldTemplateArray) {
				const normalFields = formTemplateToNormalFieldTemplates.get(formTemplateId);
				if (normalFields && tableField.tableField) {
					const parentTableField = normalFields.get(tableField.tableField);
					if (parentTableField) {
						const normalField = { ...parentTableField };
						if (normalField.tableFields) {
							normalField.tableFields.items.push({ ...tableField });
						} else {
							// Shouldn't hit here, just to make ts linter happy
							normalField.tableFields = { items: [{ ...tableField }] };
						}
						normalFields.set(tableField.tableField, normalField);
					}
				}
			}
		}
	});

	// Step 7: Create formTemplate structure
	const formTemplateIdToFormTemplate = new Map();
	for (const formTemplate of formTemplates) {
		const fieldTemplateMap = formTemplateToNormalFieldTemplates.get(formTemplate.id);
		const finalArray: FieldTemplate[] = [];
		fieldTemplateMap?.forEach((value) => finalArray.push(value));
		const finalFormTemplate: FormTemplate = { ...formTemplate };
		finalFormTemplate.fieldTemplates = { items: finalArray };
		formTemplateIdToFormTemplate.set(finalFormTemplate.id, finalFormTemplate);
	}

	// Organize all of the field types into their own respective maps
	const normalFieldInstances: Map<string, FieldInstance> = new Map();
	const tableFieldChildren: Map<string, FieldInstance[]> = new Map();
	const altInstanceMap: Map<string, FieldInstance> = new Map();
	for (const field of retrievedFieldInstances) {
		const finalFieldInstance: FieldInstance = { ...field, tableFields: { items: [] } };
		if (finalFieldInstance.imageId) {
			// Fetch the image
			const image = await getImageDocWithBinaryKeyForUpdateImageDoc(finalFieldInstance.imageId, getImageBinaries);
			finalFieldInstance.image = image;
		}
		if (finalFieldInstance.formInstance) {
			// Normal ass finalFieldInstance
			if (!normalFieldInstances.has(finalFieldInstance.id)) normalFieldInstances.set(finalFieldInstance.id, { ...finalFieldInstance });
		} else if (finalFieldInstance.tableField) {
			// Child of a table finalFieldInstance (this finalFieldInstance appears in a table)
			const tableChildren = tableFieldChildren.get(finalFieldInstance.tableField);
			if (tableChildren) {
				tableFieldChildren.set(finalFieldInstance.tableField, [...tableChildren, { ...finalFieldInstance }]);
			} else {
				tableFieldChildren.set(finalFieldInstance.tableField, [{ ...finalFieldInstance }]);
			}
		} else {
			// AltInstance
			if (!altInstanceMap.has(finalFieldInstance.id)) altInstanceMap.set(finalFieldInstance.id, { ...finalFieldInstance });
		}
	}

	// Organize the built fields by formInstance
	const formInstanceToFieldInstances: Map<string, FieldInstance[]> = new Map();

	normalFieldInstances.forEach((field) => {
		const fieldCopy: FieldInstance = { ...field };
		if (fieldCopy.formInstance) {
			if (fieldCopy.altInstanceId) {
				const altInstance = altInstanceMap.get(fieldCopy.altInstanceId);
				if (altInstance) {
					fieldCopy.altInstance = { ...altInstance };
				} else {
					console.error(`COULDN'T FIND ALT INSTANCE ${fieldCopy.altInstanceId} FOR FIELD ${fieldCopy.id}`);
				}
			}
			const children = tableFieldChildren.get(fieldCopy.id);
			if (children) {
				fieldCopy.tableFields = { items: [...children] };
			} else {
				fieldCopy.tableFields = { items: [] };
			}
			if (fieldCopy.fieldTemplateId) {
				const fieldTemplate = fieldTemplateIdToTemplate.get(fieldCopy.fieldTemplateId);
				if (fieldTemplate) fieldCopy.fieldTemplate = { ...fieldTemplate };
			}
			const fieldInstances = formInstanceToFieldInstances.get(fieldCopy.formInstance);
			if (fieldInstances) {
				formInstanceToFieldInstances.set(fieldCopy.formInstance, [...fieldInstances, fieldCopy]);
			} else {
				formInstanceToFieldInstances.set(fieldCopy.formInstance, [fieldCopy]);
			}
		}
	});

	// At this point, our fieldInstances are constructed, now we just need to attach them to their respective formInstances

	// Attach formTemplate to formInstance
	const finalFormInstances = retrievedFormInstances.map((formInstance) => {
		const finalFormInstance: FormInstance = { ...formInstance, fieldInstances: { items: [] } };
		const fieldInstances = formInstanceToFieldInstances.get(finalFormInstance.id);
		if (fieldInstances) {
			finalFormInstance.fieldInstances.items = fieldInstances;
		}
		if (finalFormInstance.formTemplateId && formTemplateIdToFormTemplate.has(finalFormInstance.formTemplateId)) {
			finalFormInstance.formTemplate = { ...formTemplateIdToFormTemplate.get(finalFormInstance.formTemplateId) };
		}
		return finalFormInstance;
	});
	return finalFormInstances;
}

/**
 * Given an array of formInstances, saves them all to the indexedDb
 * @auhtor Eric Webb <ewebb@factorearth.com>
 * @param documents The formInstances we want to save
 */
export async function bulkSaveFormInstances(documents: FormInstanceForResolver[]): Promise<void> {
	await db.formInstance.bulkPut(documents);
}

/**
 * Given the skeleton of a formInstance document, puts it in the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formInstance The formInstance document we want to create
 */
export async function createFormInstance(formInstance: FormInstanceForResolver): Promise<void> {
	await db.formInstance.put(formInstance);
}

/**
 * Given a built-out formInstance, grabs the stuff specific to the formInstance document in the schema, puts it in an object, and returns it
 * @author Eric Webb <ewebb@factorearth.com>
 * @param form The formInstance we want to create in amplify
 * @returns The parsed object to be sent to the db
 */
export function createFormInstanceForBatchResolver(form: FormInstance | FormInstanceForResolver): FormInstanceForResolver {
	const timestamp = new Date().toISOString();
	const formInstanceToCreate = {
		id: form.id,
		createdAt: form.createdAt || timestamp,
		updatedAt: form.updatedAt || timestamp,
		formGroup: form.formGroup,
		formTemplateId: form.formTemplateId,
		group: form.group,
	};
	return formInstanceToCreate;
}

/**
 * Given 2 formInstances, merges them into 1 based off of the updatedAt property
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorFormInstance The "donor" formInstance
 * @param receiverFormInstance The receiver formInstance
 * @returns The merged formInstance
 */
export function createMergedFormInstance(
	donorFormInstance: FormInstanceForResolver,
	receiverFormInstance: FormInstanceForResolver,
	donorFormTemplate: FormTemplateForResolver,
	receiverFormTemplate: FormTemplateForResolver,
): FormInstanceForResolver {
	const timeStamp = new Date();
	const leftTimeStamp = donorFormInstance.updatedAt ? new Date(donorFormInstance.updatedAt) : timeStamp;
	const rightTimeStamp = receiverFormInstance.updatedAt ? new Date(receiverFormInstance.updatedAt) : timeStamp;
	const evaluation = leftTimeStamp > rightTimeStamp;
	const correctFormTemplate = createMergedFormTemplate(donorFormTemplate, receiverFormTemplate);
	const formInstanceToCreate = {
		id: receiverFormInstance.id,
		createdAt: receiverFormInstance.createdAt || donorFormInstance.createdAt || new Date().toISOString(),
		updatedAt: evaluation ? leftTimeStamp.toISOString() : rightTimeStamp.toISOString(),
		formGroup: evaluation ? donorFormInstance.formGroup : receiverFormInstance.formGroup,
		formTemplateId: correctFormTemplate.id,
		group: receiverFormInstance.group,
	};
	return formInstanceToCreate;
}

/**
 * Given a formInstance, deletes all fieldInstances by the formInstance id, as well as the formInstance itself
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formInstance The formInstance we're deleting
 * @param formGroup The formGroup that has the formInstance we're deleting
 */
export async function deleteFormInstance(formInstance: FormInstance, formGroup: FormGroup): Promise<void> {
	// To delete a formInstance, we need to delete the fields from it, delete it itself, and remove its id from the formGroup that owns it
	// Step 1: Get all fieldInstances
	// Step 2: Create promise array for all the async operations
	// Step 3: Create a promise to delete each field
	// Step 4: Create a promise to delete the formInstance itself
	// Step 5: Remove formInstance id from formGroup order, then create promise to update it
	// Step 6: Await all promises

	// Step 1: Get all fieldInstances
	const retrievedFormInstanceFields = await getAllFieldsByFormInstanceId(formInstance.id);

	// Step 2: Create promise array for all the async operations
	const deletePromises = [];
	for (const field of retrievedFormInstanceFields) {
		// Step 3: Create a promise to delete field
		deletePromises.push(deleteFieldInstance(field));
	}

	// Step 4: Create a promise to delete the formInstance itself
	deletePromises.push(deleteFormInstanceDoc(formInstance.id));

	// Step 5: Remove formInstance id from formGroup order, then create promise to update it
	const updatedFormOrder = formGroup.formOrder.filter((id) => id !== formInstance.id);
	const updatedFormGroup = { ...formGroup, formOrder: updatedFormOrder };
	const parsedFormGroup = createFormGroupForBatchResolver(updatedFormGroup);
	deletePromises.push(updateFormGroup(parsedFormGroup));

	// Step 6: Await all promises
	await Promise.all(deletePromises);
}

/**
 * Given the id of a formInstance, deletes the document from the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the formInstance we want to delete
 */
async function deleteFormInstanceDoc(id: string): Promise<void> {
	await db.formInstance.delete(id);
}

/**
 * Given a formGroup id, queries all formInstances by it and deletes them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroupId The id of the owner formGroup
 */
export async function deleteFormInstanceByFormGroupId(formGroupId: string): Promise<void> {
	await db.formInstance.where("formGroup").equals(formGroupId).delete();
}

/**
 * Given the id of a formGroup, queries and returns all formInstance documents that are tied to the formGroup
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The formGroup id
 * @returns All formInstance documents that are tied to the formGroup
 */
export async function getFormInstancesByFormGroup(id: string): Promise<FormInstanceForResolver[]> {
	const formInstances = await db.formInstance.where("formGroup").equals(id).toArray();
	return formInstances;
}

/**
 * Given the id of a formGroup, queries and returns all formInstance documents that are tied to the formGroup with their respective formTemplates
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The formGroup id
 * @returns All formInstance documents that are tied to the formGroup
 */
export async function getFormInstanceStuffByFormGroup(id: string): Promise<FormInstanceAndFormTemplate> {
	const formInstances = await db.formInstance.where("formGroup").equals(id).toArray();
	const formTemplateIdSet: Set<string> = new Set();
	for (const instance of formInstances) {
		formTemplateIdSet.add(instance.formTemplateId);
	}
	const formTemplates = await bulkRetrieveFormTemplates(Array.from(formTemplateIdSet));
	return { formInstances, formTemplates };
}

/**
 * Queries the count of all FormInstances, multiplies the count by the average size, and returns that value
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns The filesize, in bytes, of all formInstances
 */
export async function getTotalFormInstanceDiskSize(): Promise<number> {
	const number = await db.formInstance.count();
	const AVERAGE_DOCUMENT_SIZE = 245.86;
	return number * AVERAGE_DOCUMENT_SIZE;
}

export async function getFormInstanceById(id: string): Promise<FormInstanceForResolver | undefined> {
	const formInstance = await db.formInstance.get(id);
	return formInstance;
}

/**
 * Retrieves the full form instance from the offline db.
 * @param id of the form instance to retrieve and build.
 * @returns the full form instance
 */
export async function buildFullFormInstanceByFormInstanceId(id: string) {
	// Step 1: Get the raw form instance by id
	const retrievedFormInstance = await getFormInstanceById(id);
	// Step 2: verify the form instance exists
	if (!retrievedFormInstance) {
		recordSentry({ error: `No form instance found in offline db with id ${id}` });
		return undefined;
	}
	// Step 3: Get all formTemplates from the formInstances
	const formTemplateId = retrievedFormInstance?.formTemplateId;
	// Step 4: Get all fieldTemplates by formTemplateId
	const fieldTemplatePromises = [bulkRetrieveFieldTemplatesByFormTemplate(formTemplateId)];
	const optionPromises = [bulkRetrieveOptionsByFormTemplateId(formTemplateId)];

	const [arrayOfFieldTemplateArrays, arrayOfOptionArrays, retrievedFieldInstances, formTemplates] = await Promise.all([
		await Promise.all(fieldTemplatePromises),
		await Promise.all(optionPromises),
		getAllFieldsByFormInstanceId(id),
		bulkRetrieveFormTemplates([formTemplateId]),
	]);

	// Step 5: Map options by formTemplateId. Note that the length of retrievedFormInstances, formTemplates, arrayOfFieldTemplateArrays, and arrayofOptionArrays should be the same length, where
	// index n is the complete set of everything for one form

	// Map out all Options, so we can attach options to the correct fieldTemplate by fieldTemplateId
	const fieldTemplateToOptions: Map<string, Option[]> = new Map();
	for (const optionArray of arrayOfOptionArrays) {
		for (const option of optionArray) {
			const options = fieldTemplateToOptions.get(option.fieldTemplateId);
			if (options) {
				options.push(option);
			} else {
				fieldTemplateToOptions.set(option.fieldTemplateId, [option]);
			}
		}
	}

	// Use this as quick access for the fieldInstances
	const fieldTemplateIdToTemplate: Map<string, FieldTemplateForResolver> = new Map();
	// Use these 2 to construct the formTemplate.fieldTemplates.items and the correct table structures
	const formTemplateToNormalFieldTemplates: Map<string, Map<string, FieldTemplate>> = new Map();
	const formTemplateToTableFields: Map<string, FieldTemplateForResolver[]> = new Map();
	// Step 6: Map out fieldTemplates by formTemplateId
	// Map out all fieldTemplates , assigning options. Normal fields are grouped by formTemplate key, with value being an inner map of fieldTemplate id to fieldTemplate
	// Table field fieldTemplates are kept in their own array, which we will iterate over after this and access the prior map by formTemplateId and the inner map by tableFieldId. Then,
	// we add it the that field's array. Clear as mud?
	for (const fieldTemplateArray of arrayOfFieldTemplateArrays) {
		for (const fieldTemplate of fieldTemplateArray) {
			// Makes typescript happy
			const fieldTemplateCopy: FieldTemplate = { ...fieldTemplate };
			const options = fieldTemplateToOptions.get(fieldTemplateCopy.id);
			fieldTemplateCopy.options = { items: options || [] };
			fieldTemplateCopy.tableFields = { items: [] };
			if (!fieldTemplateCopy.tableField) {
				const innerMap = formTemplateToNormalFieldTemplates.get(fieldTemplateCopy.formTemplate);
				if (innerMap) {
					innerMap.set(fieldTemplateCopy.id, { ...fieldTemplateCopy });
				} else {
					const innerMap = new Map();
					innerMap.set(fieldTemplateCopy.id, { ...fieldTemplateCopy });
					formTemplateToNormalFieldTemplates.set(fieldTemplateCopy.formTemplate, innerMap);
				}
			} else {
				const tableFields = formTemplateToTableFields.get(fieldTemplateCopy.formTemplate);
				if (tableFields) {
					formTemplateToTableFields.set(fieldTemplateCopy.formTemplate, [...tableFields, { ...fieldTemplateCopy }]);
				} else {
					formTemplateToTableFields.set(fieldTemplateCopy.formTemplate, [{ ...fieldTemplateCopy }]);
				}
			}
			fieldTemplateIdToTemplate.set(fieldTemplateCopy.id, { ...fieldTemplateCopy });
		}
	}

	//Need to handle table field retrieval now
	for (let i = 0; i < retrievedFieldInstances.length; i++) {
		const fieldInstance = retrievedFieldInstances[i];
		const fieldTemplateToCheck = fieldTemplateIdToTemplate.get(fieldInstance.fieldTemplateId || "");
		if (fieldTemplateToCheck && fieldTemplateToCheck.inputType === "table") {
			//This field has table fields so we need to get those and add them to the retrievedFieldInstances array so they get processed later
			const tableFields = await getAllTableFieldChildren(fieldInstance.id);
			retrievedFieldInstances.push(...tableFields);
		}
	}

	// Loop through the map of the formTemplate's fieldTemplates that are children of fields. Find the parent tableField in the formTemplateToNormalFieldTemplates map. If that map has
	// the parent field, copy it, push a copy of the table field into the tableFields array, and update the map
	formTemplateToTableFields.forEach((tableFieldTemplateArray, formTemplateId) => {
		if (formTemplateToNormalFieldTemplates.has(formTemplateId)) {
			for (const tableField of tableFieldTemplateArray) {
				const normalFields = formTemplateToNormalFieldTemplates.get(formTemplateId);
				if (normalFields && tableField.tableField) {
					const parentTableField = normalFields.get(tableField.tableField);
					if (parentTableField) {
						const normalField = { ...parentTableField };
						if (normalField.tableFields) {
							normalField.tableFields.items.push({ ...tableField });
						} else {
							// Shouldn't hit here, just to make ts linter happy
							normalField.tableFields = { items: [{ ...tableField }] };
						}
						normalFields.set(tableField.tableField, normalField);
					}
				}
			}
		}
	});

	// Step 7: Create formTemplate structure
	const formTemplateIdToFormTemplate = new Map();
	for (const formTemplate of formTemplates) {
		const fieldTemplateMap = formTemplateToNormalFieldTemplates.get(formTemplate.id);
		const finalArray: FieldTemplate[] = [];
		fieldTemplateMap?.forEach((value) => finalArray.push(value));
		const finalFormTemplate: FormTemplate = { ...formTemplate };
		finalFormTemplate.fieldTemplates = { items: finalArray };
		formTemplateIdToFormTemplate.set(finalFormTemplate.id, finalFormTemplate);
	}

	// Organize all of the field types into their own respective maps
	const normalFieldInstances: Map<string, FieldInstance> = new Map();
	const tableFieldChildren: Map<string, FieldInstance[]> = new Map();
	const altInstanceMap: Map<string, FieldInstance> = new Map();
	for (const field of retrievedFieldInstances) {
		const finalFieldInstance: FieldInstance = { ...field, tableFields: { items: [] } };
		if (finalFieldInstance.imageId) {
			// Fetch the image
			const image = await getImageDocWithBinaryKeyForUpdateImageDoc(finalFieldInstance.imageId, true);
			finalFieldInstance.image = image;
		}
		if (finalFieldInstance.formInstance) {
			if (!normalFieldInstances.has(finalFieldInstance.id)) normalFieldInstances.set(finalFieldInstance.id, { ...finalFieldInstance });
		} else if (finalFieldInstance.tableField) {
			// Child of a table finalFieldInstance (this finalFieldInstance appears in a table)
			const tableChildren = tableFieldChildren.get(finalFieldInstance.tableField);
			if (tableChildren) {
				tableFieldChildren.set(finalFieldInstance.tableField, [...tableChildren, { ...finalFieldInstance }]);
			} else {
				tableFieldChildren.set(finalFieldInstance.tableField, [{ ...finalFieldInstance }]);
			}
		} else {
			// AltInstance
			if (!altInstanceMap.has(finalFieldInstance.id)) altInstanceMap.set(finalFieldInstance.id, { ...finalFieldInstance });
		}
	}

	// Organize the built fields by formInstance
	const formInstanceToFieldInstances: Map<string, FieldInstance[]> = new Map();

	normalFieldInstances.forEach((field) => {
		const fieldCopy: FieldInstance = { ...field };
		if (fieldCopy.formInstance) {
			if (fieldCopy.altInstanceId) {
				const altInstance = altInstanceMap.get(fieldCopy.altInstanceId);
				if (altInstance) {
					fieldCopy.altInstance = { ...altInstance };
				} else {
					console.error(`COULDN'T FIND ALT INSTANCE ${fieldCopy.altInstanceId} FOR FIELD ${fieldCopy.id}`);
				}
			}
			const children = tableFieldChildren.get(fieldCopy.id);
			if (children) {
				fieldCopy.tableFields = { items: [...children] };
			} else {
				fieldCopy.tableFields = { items: [] };
			}
			if (fieldCopy.fieldTemplateId) {
				const fieldTemplate = fieldTemplateIdToTemplate.get(fieldCopy.fieldTemplateId);
				if (fieldTemplate) fieldCopy.fieldTemplate = { ...fieldTemplate };
			}
			const fieldInstances = formInstanceToFieldInstances.get(fieldCopy.formInstance);
			if (fieldInstances) {
				formInstanceToFieldInstances.set(fieldCopy.formInstance, [...fieldInstances, fieldCopy]);
			} else {
				formInstanceToFieldInstances.set(fieldCopy.formInstance, [fieldCopy]);
			}
		}
	});

	// At this point, our fieldInstances are constructed, now we just need to attach them to their respective formInstances

	// Attach formTemplate to formInstance
	const finalFormInstance: FormInstance = { ...retrievedFormInstance, fieldInstances: { items: [] } };
	const fieldInstances = formInstanceToFieldInstances.get(finalFormInstance.id);
	if (fieldInstances) {
		finalFormInstance.fieldInstances.items = fieldInstances;
	}
	if (finalFormInstance.formTemplateId && formTemplateIdToFormTemplate.has(finalFormInstance.formTemplateId)) {
		finalFormInstance.formTemplate = { ...formTemplateIdToFormTemplate.get(finalFormInstance.formTemplateId) };
	}

	return finalFormInstance;
}
